import type { IconName } from '@d-edge/overnight-hotelier-react'
import type { Tone } from '@/components/ui/Badge'
import type { AttentionItem, Severity } from './attention'

export const SEVERITY: Record<Severity, { label: string; tone: Tone; icon: 'octagon-exclamation' | 'triangle-exclamation' | 'circle-info' }> = {
  critical: { label: 'Critical', tone: 'danger', icon: 'octagon-exclamation' },
  warning: { label: 'Warning', tone: 'warn', icon: 'triangle-exclamation' },
  info: { label: 'To watch', tone: 'info', icon: 'circle-info' },
}

// Tiles show a few words, not the full sentence of the item; unknown ids fall back to the title.
const SHORT_LABEL: Record<string, string> = {
  'rs-delay': 'Screening running late',
  'rs-success': 'Success ratio (24 h)',
  'rs-low': 'POSs with low success',
  'rs-declining': 'POSs below 36 h ago',
  'rs-in-progress': 'POSs still in progress',
  'rs-disabled-priority': 'Priority POSs with sync off',
  'sentinel-success': 'Success ratio (24 h)',
  'sentinel-sites': 'Sites with bad success',
  'sentinel-disabled': 'POSs disabled',
  'price-down': 'API health',
  'price-errors': 'API error rate',
  'price-none': 'Recommendations generated',
  'blacklist-heavy': 'POSs with heavy blacklists',
  unavailable: 'Data sources unavailable',
}

/** Items whose headline figure is the ratio or delay quoted in their title rather than a count. */
const MEASURED = new Set(['rs-delay', 'rs-success', 'sentinel-success', 'price-errors'])
const MEASURE = /(\d+(?:\.\d+)?%|\d+d \d+h|\d+h \d+m|\d+m \d+s)/

export function tileLabel(item: AttentionItem): string {
  return SHORT_LABEL[item.id] ?? item.title
}

/** The one figure a tile shows: the measure quoted in the title, or the number of entries. */
export function tileValue(item: AttentionItem): string {
  if (item.id === 'price-down') return 'Down'
  if (item.id === 'price-none') return '0'
  const measure = MEASURE.exec(item.title)?.[1]
  if (MEASURED.has(item.id) && measure) return measure
  if (item.entries.length > 0) return String(item.entries.length)
  return measure ?? '!'
}

// One kit icon per check so a tile can be recognised before its label is read.
const TILE_ICON: Record<string, IconName> = {
  'rs-delay': 'alarm-clock',
  'rs-success': 'percent',
  'rs-low': 'chart-column',
  'rs-declining': 'chart-line',
  'rs-in-progress': 'circle-notch',
  'rs-disabled-priority': 'power-off',
  'sentinel-success': 'percent',
  'sentinel-sites': 'chart-column',
  'sentinel-disabled': 'power-off',
  'price-down': 'circle-stop',
  'price-errors': 'triangle-exclamation',
  'price-none': 'tags',
  'blacklist-heavy': 'ban',
  unavailable: 'question',
}

export function tileIcon(item: AttentionItem): IconName {
  return TILE_ICON[item.id] ?? 'circle-info'
}
